import RightArrow from '@public/icons/arrow-small-right.svg';
import MobileImage1 from '@public/why-alter/equipment-1.webp';
import MobileImage2 from '@public/why-alter/equipment-2.webp';
import MobileImage3 from '@public/why-alter/equipment-3.webp';
import MobileImage4 from '@public/why-alter/equipment-4.webp';
import MobileImage5 from '@public/why-alter/equipment-5.webp';
import DesktopImage from '@public/why-alter/wide-render.webp';
import DesktopImageText from '@public/why-alter/wide-render-text.webp';
import clsx from 'clsx';
import { useInView } from 'framer-motion';
import type { StaticImageData } from 'next/image';
import Image from 'next/image';
import React, { useRef, useState } from 'react';

import useWindowDimensions from '@/hooks/useWindowDimensions';
import { getTailwindBreakpoints } from '@/utils/misc';

const SLIDES = [
  {
    src: MobileImage1,
    alt: 'Alter System',
  },
  {
    src: MobileImage2,
    alt: 'Alter Screen',
    text: (
      <>
        The <b>Alter</b> Screen provides hyper-personalized workouts with top trainers and heart
        rate training—tailored just for you
      </>
    ),
  },
  {
    src: MobileImage3,
    alt: 'Foam Roller',
    text: <>Foam Roller, Workout Bands, and Yoga Mat are included with your screen</>,
  },
  {
    src: MobileImage4,
    alt: 'Alter Swab',
    text: (
      <>
        Comprehensive DNA-Powered{'\n'}
        <b>Health and Fitness Insights Report</b>
      </>
    ),
  },
  {
    src: MobileImage5,
    alt: 'Alter App and Signal',
    text: (
      <>
        The <b>Alter Wearable</b> captures your health data, while the <b>App</b> turns it into
        personalized recommendations and coaching for a seamless, holistic wellness experience
      </>
    ),
  },
];

interface MobileCarouselItemProps {
  src: StaticImageData;
  alt: string;
  text?: React.ReactNode;
  show: boolean;
}

const MobileCarouselItem = ({ src, alt, text, show }: MobileCarouselItemProps) => {
  return (
    <div
      className={clsx(
        'absolute flex flex-col justify-end w-full h-full transition-opacity duration-500 md:hidden',
        show ? 'opacity-100' : 'opacity-0'
      )}
    >
      {text && (
        <p className="absolute top-12 left-0 right-0 max-w-[38ch] mx-auto px-4 text-center font-light whitespace-pre-line">
          {text}
        </p>
      )}
      <Image
        src={src}
        alt={alt}
        width={750}
        height={1000}
        className="object-contain"
        quality={90}
        loading="lazy"
      />
    </div>
  );
};

const MobileCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const nextSlide = () => setCurrentSlide((prev) => prev + 1);
  const prevSlide = () => setCurrentSlide((prev) => prev - 1);
  return (
    <div className="relative aspect-[414/552] h-full w-full md:hidden">
      {SLIDES.map((slide) => (
        <MobileCarouselItem
          key={slide.alt}
          src={slide.src}
          alt={slide.alt}
          text={slide.text}
          show={SLIDES[currentSlide].alt === slide.alt}
        />
      ))}
      {currentSlide > 0 && (
        <button
          className="pagination-arrow-container w-8 h-8 absolute top-1/2 -translate-y-1/2 left-4"
          onClick={prevSlide}
          aria-label="Previous"
        >
          <Image
            src={RightArrow}
            alt="Previous"
            width={18}
            height={18}
            className="rotate-180 mx-auto"
            loading="lazy"
          />
        </button>
      )}
      {currentSlide < SLIDES.length - 1 && (
        <button
          className="pagination-arrow-container w-8 h-8 absolute top-1/2 -translate-y-1/2 right-4"
          onClick={nextSlide}
          aria-label="Next"
        >
          <Image src={RightArrow} alt="next" width={18} height={18} className="mx-auto" />
        </button>
      )}
      <div className="absolute bottom-4 w-full flex justify-center gap-2 mx-auto">
        {SLIDES.map((item, index) => (
          <div
            key={item.alt}
            className={clsx(
              'h-2 w-2 rounded-full transition-colors duration-500',
              index === currentSlide ? 'bg-alter-black' : 'bg-alter-black-10'
            )}
          />
        ))}
      </div>
    </div>
  );
};

const Equipment: React.FC = () => {
  const { width } = useWindowDimensions();
  const isMobile = !!width && width < getTailwindBreakpoints('md');
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: false,
    amount: 0.8,
  });

  return (
    <div
      ref={ref}
      className="relative w-full overflow-hidden bg-gradient-to-tr from-[#EBD7D1] from-60% to-[#CBC4C2] md:aspect-auto"
    >
      <p className="max-w-[18ch] mt-14 mx-auto font-light leading-snug text-center text-[32px] md:mb-7 lg:mb-12 lg:text-[48px]">
        All-in-one system starting at $99/month
      </p>
      <div className="relative w-full max-w-[1280px] mx-auto">
        {isMobile ? (
          <MobileCarousel />
        ) : (
          <>
            <Image
              src={DesktopImage}
              alt="Equipment Desktop View"
              height={1100}
              width={2200}
              className="object-cover w-full h-full"
              quality={90}
            />
            <Image
              fill
              src={DesktopImageText}
              alt="Equipment Desktop View Text bubbles"
              className={clsx(
                'object-cover transition-opacity duration-700 lg:pl-[125px] lg:pb-[95px] lg:pr-[50px] pl-[100px] pb-[70px] pr-[25px]',
                isInView ? 'opacity-100' : 'opacity-0'
              )}
              quality={100}
              loading="lazy"
            />
          </>
        )}
      </div>
    </div>
  );
};

export default Equipment;
